import React, { useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import classnames from 'classnames';
import { MutedBackground } from 'scy/Background';
import { MediumContainer } from 'scy/layouts';
import { H3, Paragraph } from 'scy/text';
import { textCenter, plainList, width50 } from 'scy/utils.mod.scss';
import { hidden, tablet_block } from 'scy/display.mod.scss';
import { flex } from 'scy/flex.mod.scss';
import padding from 'scy/padding.mod.scss';
import margin from 'scy/margin.mod.scss';
import styles from './faq.mod.scss';
import { withConjunction } from 'shared/utils';

export default function List() {
  return (
    <MutedBackground
      tag="section"
      className={classnames(
        hidden,
        tablet_block,
        padding.top_lg,
        padding.bottom_xl
      )}
    >
      <H3 marginClass={margin.lg} className={textCenter}>
        FAQs
      </H3>
      <MediumContainer
        tag="ul"
        className={classnames(flex, margin.sides_auto, plainList)}
      >
        <ColumnOne />
        <ColumnTwo />
      </MediumContainer>
    </MutedBackground>
  );
}

const Column = ({ children }) => (
  <div className={classnames(margin.left_lg, margin.right_sm, width50)}>
    {children}
  </div>
);

const Question = ({ children }) => <Paragraph tag="div">{children}</Paragraph>;

const Answer = React.forwardRef(({ style, children }, ref) => (
  <div className={styles.answer} style={style} ref={ref}>
    {children}
  </div>
));

function Cities() {
  const cities = useSelector(state => state.cities || {});
  const list = cities.list || [];
  const names = list.map(({ name }) => name);
  const phrase = withConjunction(names, '&');

  return (
    <ListItem>
      {answerProps => (
        <>
          <Question>In which cities does Saucey deliver?</Question>
          <Answer {...answerProps}>
            {phrase
              ? `We delivery on-demand to ${phrase}`
              : 'Saucey offers 30-minute alcohol delivery'}
            . We also offer 2-day and ground shipping in California and New
            York.
          </Answer>
        </>
      )}
    </ListItem>
  );
}

function ColumnOne() {
  return (
    <Column>
      <Cities />

      <ListItem>
        {answerProps => (
          <>
            <Question>Why aren’t you in my city?</Question>
            <Answer {...answerProps}>
              We’re rapidly expanding and we’ll let you know when we arrive in
              yours. Until then, enjoy{' '}
              <a
                href="https://milehidistilling.com/moonshine-recipes/"
                rel="noopener noreferrer"
                target="_blank"
              >
                this recipe for Moonshine
              </a>
              .
            </Answer>
          </>
        )}
      </ListItem>

      <ListItem>
        {answerProps => (
          <>
            <Question>What are Saucey’s hours?</Question>
            <Answer {...answerProps}>
              We’re open for 30-minute delivery:
              <ul className={styles.sublist}>
                <li>8am-2am</li>
              </ul>
              <div>* You can also schedule an order in advance.</div>
              <div>** Hours vary by location and are subject to change.</div>
            </Answer>
          </>
        )}
      </ListItem>

      <ListItem>
        {answerProps => (
          <>
            <Question>How much is the delivery fee?</Question>
            <Answer {...answerProps}>
              Saucey charges a $4.99 delivery fee to help pay our hardworking
              couriers for their service.
            </Answer>
          </>
        )}
      </ListItem>

      <ListItem>
        {answerProps => (
          <>
            <Question>
              Do I need to be 21 to order alcohol from Saucey?
            </Question>
            <Answer {...answerProps}>
              Yes. We check ID, on EVERY. SINGLE. ORDER. As part of our delivery
              process, we have ID scanners that work for your drivers license,
              state ID or passport. You must be 21+ to receive your delivery.
            </Answer>
          </>
        )}
      </ListItem>
    </Column>
  );
}

function ColumnTwo() {
  return (
    <Column>
      <ListItem>
        {answerProps => (
          <>
            <Question>Is there an app for alcohol delivery?</Question>
            <Answer {...answerProps}>
              Yep! We have both an{' '}
              <a href="https://play.google.com/store/apps/details?id=com.saucey&hl=en">
                Android
              </a>{' '}
              and an{' '}
              <a href="https://itunes.apple.com/us/app/saucey-the-alcohol-delivery-app/id869556845?mt=8">
                iOS
              </a>{' '}
              app. Download for free and get it poppin’.
            </Answer>
          </>
        )}
      </ListItem>
      <ListItem>
        {answerProps => (
          <>
            <Question>Does Saucey charge a small cart fee?</Question>
            <Answer {...answerProps}>
              While there is no order minimum on Saucey, we do charge a small
              cart fee which varies across our different delivery zones.
            </Answer>
          </>
        )}
      </ListItem>
      <ListItem>
        {answerProps => (
          <>
            <Question>How does alcohol delivery work?</Question>
            <Answer {...answerProps}>
              You shop your favorite beer, wine, spirits and mixers. We’ll
              deliver your order right to your door. Make sure you have your ID
              handy for the delivery. We’ll scan your ID to verify you are 21 or
              older.
            </Answer>
          </>
        )}
      </ListItem>

      <ListItem>
        {answerProps => (
          <>
            <Question>Will you give me free alcohol for a year?</Question>
            <Answer {...answerProps}>
              Maybe, what’s in it for us? Tell us{' '}
              <a href="https://twitter.com/sauceyapp">@SauceyApp</a>.
            </Answer>
          </>
        )}
      </ListItem>

      <ListItem>
        {answerProps => (
          <>
            <Question>When should I use Saucey?</Question>
            <Answer {...answerProps}>
              Whenever you Netflix and chill, birthdays, Tuesdays, bachelor
              parties, Thirsty Thursdays, BYOB parties, #Selfcare Sundays…
              literally all the time.
            </Answer>
          </>
        )}
      </ListItem>
    </Column>
  );
}

function ListItem({ children }) {
  const [open, setOpen] = useState(false);
  const answerRef = useRef();

  function toggle(event) {
    // don't change state if clicked link
    if (event.target.tagName === 'A') return;
    setOpen(!open);
  }

  function getMaxHeight() {
    if (!open) return '0px';

    const answerNode = answerRef.current;
    if (!answerNode) return;

    const clone = answerNode.cloneNode(true);

    const { width, fontSize, letterSpacing } = window.getComputedStyle(
      answerNode
    );
    clone.style.width = width;
    clone.style.fontSize = fontSize;
    clone.style.letterSpacing = letterSpacing;

    clone.style.position = 'absolute';
    clone.style.left = '10000px'; // render off-screen
    clone.style.maxHeight = '10000px';
    clone.style.transition = null;
    document.body.appendChild(clone);
    const maxHeight = clone.clientHeight;
    clone.remove();

    return maxHeight;
  }

  const answerProps = {
    style: {
      maxHeight: getMaxHeight(),
      overflow: 'hidden',
      transition: 'max-height 200ms ease-out'
    },
    ref: answerRef
  };

  return (
    <li
      onClick={toggle}
      className={`${styles.item} ${open ? styles.open : styles.closed}`}
    >
      {children(answerProps)}
    </li>
  );
}
